import { cn } from '@/lib/utils'

/**
 * Pixel sprites rendered as glossy beads: every pixel is its own rounded
 * voxel with a lit top-left, a shaded bottom-right and a specular glint, so
 * the silhouette reads as retro but the surface reads as sculpted.
 *
 * Keys: a = lavender shell, p = lime core, d = deep console plastic,
 * f = pale highlight. "." is empty.
 */
export const SPRITES = {
  // A compact chip creature with pins — entry tier.
  critter: [
    '..a.a..a.a..',
    '.dddddddddd.',
    'adfdddddddda',
    '.ddppddppdd.',
    'addppddppdda',
    '.dddddddddd.',
    'adddaaaaddda',
    '.dddddddddd.',
    '..a.a..a.a..',
  ],
  // The power-unit robot — mid tier and the hero mascot.
  unit: [
    '.....pp.....',
    '.....aa.....',
    '..aaaaaaaa..',
    '.afaaaaaaaa.',
    '.adddddddda.',
    '.adpddddpda.',
    '.adpddddpda.',
    '.adddddddda.',
    '.aaaaaaaaaa.',
    '..aaaaaaaa..',
    '...aa..aa...',
    '..aaa..aaa..',
  ],
  // The twin-antenna titan — high tier.
  titan: [
    '...p......p...',
    '...a......a...',
    '..aaaaaaaaaa..',
    '.aafaaaaaaaaa.',
    '.adddddddddda.',
    '.adppddddppda.',
    '.adppddddppda.',
    '.adddaaaaddda.',
    '.aaaaaaaaaaaa.',
    'aa.aaaaaaaa.aa',
    'a..aaaaaaaa..a',
    '...aaa..aaa...',
    '..aaaa..aaaa..',
  ],
} as const

export type SpriteName = keyof typeof SPRITES

const GRADIENTS: Record<string, [string, string, string]> = {
  a: ['stop-foreground', 'stop-accent', 'stop-background'],
  p: ['stop-foreground', 'stop-primary', 'stop-background'],
  d: ['stop-accent', 'stop-secondary', 'stop-background'],
  f: ['stop-foreground', 'stop-foreground', 'stop-accent'],
}

const STOP_OPACITY: Record<string, [number, number, number]> = {
  a: [0.85, 1, 1],
  p: [0.8, 1, 1],
  d: [0.45, 1, 1],
  f: [1, 1, 1],
}

export function PixelSprite({
  name,
  uid,
  title,
  className,
}: {
  name: SpriteName
  /** Unique per instance on the page, so gradient ids never collide. */
  uid: string
  title?: string
  className?: string
}) {
  const map = SPRITES[name]
  const rows = map.length
  const cols = map[0].length
  const keys = [...new Set(map.join('').replace(/\./g, ''))]

  return (
    <svg
      viewBox={`0 0 ${cols} ${rows}`}
      className={cn('block h-auto w-full overflow-visible', className)}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <defs>
        {keys.map((key) => (
          <linearGradient key={key} id={`${uid}-${key}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" className={GRADIENTS[key][0]} stopOpacity={STOP_OPACITY[key][0]} />
            <stop offset="0.45" className={GRADIENTS[key][1]} stopOpacity={STOP_OPACITY[key][1]} />
            <stop offset="1" className={GRADIENTS[key][2]} stopOpacity={STOP_OPACITY[key][2]} />
          </linearGradient>
        ))}
      </defs>
      {map.map((row, y) =>
        row.split('').map((key, x) =>
          key === '.' ? null : (
            <g key={`${x}-${y}`}>
              <rect x={x + 0.04} y={y + 0.04} width={0.92} height={0.92} rx={0.18} fill={`url(#${uid}-${key})`} />
              <rect x={x + 0.16} y={y + 0.14} width={0.36} height={0.16} rx={0.08} className="fill-foreground" opacity={0.55} />
            </g>
          ),
        ),
      )}
    </svg>
  )
}
